import { findFirstNonEmptyString, notEmpty } from "@product/scmp-sdk";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseImage } from "~/components/common/base-image";
import type { MoreOnThisWidgetEvents } from "~/components/tracking/google-analytics/types";
import type { RecirculationWidgetImpressionEvent } from "~/components/tracking/google-analytics-4/types";
import { useTrackImpressionEventByIntersection } from "~/components/tracking/hooks";
import { getAbsoluteUrl } from "~/lib/utils";
import type { articleMoreOnThisListArticle$key } from "~/queries/__generated__/articleMoreOnThisListArticle.graphql";

import { useTracking } from "./hooks";
import type { StyledProps } from "./styles";
import {
  Item,
  StyledContainer,
  StyledEntityLink,
  StyledFigure,
  StyledHeader,
  StyledTitle,
} from "./styles";

export type Props = {
  className?: string;
  reference: articleMoreOnThisListArticle$key;
} & Partial<PropsFromStyledProps<StyledProps>>;

export const ArticleMoreOnThisList: FunctionComponent<Props> = ({
  className,
  reference: reference_,
  variant = "horizontal",
}) => {
  const { trackClick } = useTracking();

  const { moreOnThisArticles } = useFragment(
    graphql`
      fragment articleMoreOnThisListArticle on Article {
        moreOnThisArticles {
          ...entityLink
          entityId
          headline
          socialHeadline
          urlAlias
          images {
            url
            style(filter: { style: "237x147" }) {
              url
            }
          }
        }
      }
    `,
    reference_,
  );

  const articles = moreOnThisArticles?.filter(notEmpty);

  const impressionEvent = useCallback<() => MoreOnThisWidgetEvents>(
    () => ({
      action: "More on this Story/Impression",
      category: "More on this Story",
      label: "",
    }),
    [],
  );

  const ga4ImpressionEvent = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        page_type: "article",
        widget_name: "read_more",
      },
      subcategory: "widget",
    }),
    [],
  );

  const { captureTrackImpressionEventTargetElement } = useTrackImpressionEventByIntersection({
    getEvent: impressionEvent,
    getGa4Event: ga4ImpressionEvent,
    options: {
      intersectionThreshold: [0.1],
      isSendGA4Tracking: true,
      isSendGATracking: true,
      shouldSendOnce: true,
      shouldThrottle: true,
    },
  });

  const renderImage = (article: Article) => {
    const mainImage = first(article.images);
    const source = mainImage?.style?.url ?? mainImage?.url;
    return (
      source && (
        <BaseImage
          $aspectRatio="237 / 147"
          $height="150px"
          $objectFit="cover"
          $width="220px"
          alt={article.headline ?? undefined}
          src={source}
          disableLinkingData
        />
      )
    );
  };

  const renderTitle = (article: Article) => (
    <StyledTitle>{findFirstNonEmptyString(article.socialHeadline, article.headline)}</StyledTitle>
  );

  if (!articles?.length) return null;

  return (
    <StyledContainer
      $variant={variant}
      className={className}
      ref={captureTrackImpressionEventTargetElement}
    >
      {articles.map((article, index) => (
        <Item key={article.entityId}>
          <StyledEntityLink
            reference={article}
            onClick={() => {
              trackClick(getAbsoluteUrl(article?.urlAlias), article.entityId, `${index + 1}`);
            }}
          >
            <StyledFigure>
              <StyledHeader>Read more</StyledHeader>
              {renderImage(article)}
              {renderTitle(article)}
            </StyledFigure>
          </StyledEntityLink>
        </Item>
      ))}
    </StyledContainer>
  );

  type Article = Exclude<typeof articles, undefined>[number];
};

ArticleMoreOnThisList.displayName = "ArticleMoreOnThisList";
